/**
 * "Benachrichtigen Sie mich, wenn verfügbar" — swaps the buy buttons for a
 * back-in-stock button while the selected variant is sold out, and opens the
 * STOQ (Restock Rocket) sign-up modal on click.
 *
 * The theme does not re-render the button row on variant change; it only
 * toggles the existing add-to-cart button. So this element:
 * - reads the selected variant from the form's hidden `id` input,
 * - looks up its availability in the variant list rendered next to it,
 * - re-checks whenever the add-to-cart button's state changes or an option
 *   input changes.
 *
 * STOQ API: window._RestockRocket.openModal(productData, variantId)
 * https://docs.stoqapp.com/frontend/docs/javascript-api/
 */
class RestockNotify extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.productForm = this.closest('product-form-component');
    this.button = this.querySelector('[data-restock-button]');

    try {
      this.product = JSON.parse(this.querySelector('[data-restock-product]')?.textContent || '{}');
    } catch {
      this.product = {};
    }
    this.availability = new Map((this.product.variants || []).map((v) => [String(v.id), v.available]));

    this.onClick = () => this.open();
    this.onChange = () => queueMicrotask(() => this.update());
    this.button?.addEventListener('click', this.onClick);
    this.productForm?.addEventListener('change', this.onChange);
    document.addEventListener('change', this.onChange);

    // The add-to-cart button gets `disabled` toggled on variant change.
    this.observer = new MutationObserver(() => this.update());
    const watched = this.productForm || this.form;
    if (watched) {
      this.observer.observe(watched, { attributes: true, subtree: true, attributeFilter: ['disabled', 'hidden', 'value'] });
    }

    this.update();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    this.productForm?.removeEventListener('change', this.onChange);
    document.removeEventListener('change', this.onChange);
    this.observer?.disconnect();
  }

  get variantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="id"]'));
    return input?.value || String(this.product.selected_variant_id || '');
  }

  update() {
    const id = this.variantId;
    // Unknown variant (e.g. an unavailable combination) counts as sold out.
    const available = this.availability.get(id) === true;
    this.toggleAttribute('data-active', !available);
  }

  open() {
    const variantId = this.variantId;
    if (!variantId) return;

    const productData = {
      id: this.product.id,
      title: this.product.title,
      variants: this.product.variants || [],
    };

    const call = () => window._RestockRocket?.openModal?.(productData, String(variantId));

    if (typeof window._RestockRocket?.openModal === 'function') {
      call();
      return;
    }

    // STOQ loads after the theme; open as soon as it is ready.
    this.button?.setAttribute('aria-busy', 'true');
    let done = false;
    const onLoaded = () => {
      if (done) return;
      done = true;
      this.button?.removeAttribute('aria-busy');
      call();
    };
    window.addEventListener('stoq:loaded', onLoaded, { once: true });
    setTimeout(() => {
      window.removeEventListener('stoq:loaded', onLoaded);
      if (typeof window._RestockRocket?.openModal === 'function') {
        onLoaded();
      } else {
        done = true;
        this.button?.removeAttribute('aria-busy');
      }
    }, 2500);
  }
}

if (!customElements.get('restock-notify')) {
  customElements.define('restock-notify', RestockNotify);
}
